import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './CreateCampaignModal.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';
import { AiOutlineClose } from 'react-icons/ai';
import { toast } from 'react-toastify';

const CreateCampaignModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    Title: '',
    Body: '',
    targetAudience: 'all',
    Schedule: '',
    customRecipients: []
  });

  const [customRecipientInput, setCustomRecipientInput] = useState({
    name: '',
    mobile: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [smsCount, setSmsCount] = useState(1);

  const userId = localStorage.getItem('id');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (formData.Body) {
      const hasUnicode = /[^\u0000-\u007F]/.test(formData.Body);
      const charsPerSMS = hasUnicode ? 70 : 160;
      setCharCount(formData.Body.length);
      setSmsCount(Math.ceil(formData.Body.length / charsPerSMS));
    } else {
      setCharCount(0);
      setSmsCount(1);
    }
  }, [formData.Body]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleAddCustomRecipient = () => {
    if (!customRecipientInput.mobile.trim()) {
      setError('Mobile number is required');
      return;
    }

    const newRecipient = {
      name: customRecipientInput.name.trim() || 'Customer',
      mobile: customRecipientInput.mobile.trim()
    };

    setFormData(prev => ({
      ...prev,
      customRecipients: [...prev.customRecipients, newRecipient]
    }));

    setCustomRecipientInput({ name: '', mobile: '' });
    setError('');
  };

  const handleRemoveCustomRecipient = (index) => {
    setFormData(prev => ({
      ...prev,
      customRecipients: prev.customRecipients.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.Title.trim()) {
      setError('Campaign title is required');
      return;
    }

    if (!formData.Body.trim()) {
      setError('Message body is required');
      return;
    }

    if (formData.Body.length > 1000) {
      setError('Message body cannot exceed 1000 characters');
      return;
    }

    if (formData.targetAudience === 'custom' && formData.customRecipients.length === 0) {
      setError('Please add at least one recipient for custom audience');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        Title: formData.Title.trim(),
        Body: formData.Body.trim(),
        targetAudience: formData.targetAudience
      };

      if (formData.Schedule) {
        payload.Schedule = new Date(formData.Schedule).toISOString();
      }

      if (formData.targetAudience === 'custom') {
        payload.customRecipients = formData.customRecipients;
      }

      const response = await axios.post(
        `${BASEURL}superadmin/sms-marketing/${userId}`,
        payload,
        { headers: { token } }
      );

      if (response.data.success) {
        toast.success('Campaign created successfully!');
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          Title: '',
          Body: '',
          targetAudience: 'all',
          Schedule: '',
          customRecipients: []
        });
      } else {
        const errorMsg = response.data.error || 'Failed to create campaign';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create campaign';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Create SMS Campaign</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <AiOutlineClose />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <div className={styles.error}>{error}</div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="Title">Campaign Title *</label>
            <input
              type="text"
              id="Title"
              name="Title"
              value={formData.Title}
              onChange={handleInputChange}
              placeholder="e.g., New Year Offer 2025"
              required
              maxLength={200}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="Body">Message Body *</label>
            <textarea
              id="Body"
              name="Body"
              value={formData.Body}
              onChange={handleInputChange}
              placeholder="Enter your SMS message here..."
              required
              rows={5}
              maxLength={1000}
            />
            <div className={styles.charCounter}>
              <span>{charCount} / 1000 characters</span>
              <span className={styles.smsCount}>≈ {smsCount} SMS credit{smsCount > 1 ? 's' : ''}</span>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="targetAudience">Target Audience *</label>
            <select
              id="targetAudience"
              name="targetAudience"
              value={formData.targetAudience}
              onChange={handleInputChange}
              required
            >
              <option value="all">All Users</option>
              <option value="trial">Trial Users</option>
              <option value="paid">Paid Users</option>
              <option value="expired">Expired Users</option>
              <option value="custom">Custom Recipients</option>
            </select>
          </div>

          {formData.targetAudience === 'custom' && (
            <div className={styles.customRecipientsSection}>
              <label>Add Recipients</label>
              <div className={styles.recipientInputRow}>
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={customRecipientInput.name}
                  onChange={(e) => setCustomRecipientInput(prev => ({ ...prev, name: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Mobile Number *"
                  value={customRecipientInput.mobile}
                  onChange={(e) => setCustomRecipientInput(prev => ({ ...prev, mobile: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={handleAddCustomRecipient}
                  className={styles.addButton}
                >
                  Add
                </button>
              </div>

              {formData.customRecipients.length > 0 && (
                <div className={styles.recipientsList}>
                  <p className={styles.recipientsCount}>
                    {formData.customRecipients.length} recipient{formData.customRecipients.length > 1 ? 's' : ''}
                  </p>
                  {formData.customRecipients.map((recipient, index) => (
                    <div key={index} className={styles.recipientItem}>
                      <span>{recipient.name} - {recipient.mobile}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomRecipient(index)}
                        className={styles.removeButton}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="Schedule">Schedule (Optional)</label>
            <input
              type="datetime-local"
              id="Schedule"
              name="Schedule"
              value={formData.Schedule}
              onChange={handleInputChange}
              min={new Date().toISOString().slice(0, 16)}
            />
            <small className={styles.helpText}>
              Leave empty to save as draft. Set a future date to schedule.
            </small>
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;
